import { Coffee, Plus, RotateCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { CapabilityGate } from '../components/CapabilityGate';
import { PendencyCreateModal } from '../components/PendencyCreateModal';
import {
  applyClientFilters,
  deriveCounters,
  groupByDeadline,
} from '../features/pendencies/pendencies-adapter';
import { PendencyBanners } from '../features/pendencies/PendencyBanners';
import {
  PendencyCard,
  type PendencyActionKind,
  type PendencyDueState,
} from '../features/pendencies/PendencyCard';
import { PendencyDrawer } from '../features/pendencies/PendencyDrawer';
import {
  EMPTY_FILTERS,
  PendencyFilterRail,
  type PendencyFilters,
} from '../features/pendencies/PendencyFilterRail';
import { PendencyMetricsPanel } from '../features/pendencies/PendencyMetricsPanel';
import { PostponePendencyModal } from '../features/pendencies/PostponePendencyModal';
import { ResolvePendencyModal } from '../features/pendencies/ResolvePendencyModal';
import { useCapability } from '../hooks/use-capability';
import {
  useListMyPendencies,
  useMyPendencyMetrics,
  usePostponePendency,
  useResolvePendency,
  useResumePendency,
  useUpdatePendency,
} from '../hooks/use-pendencies';
import { useToastStore } from '../stores/toast-store';

import type { PendencyResolutionOutcome, PendencyWireItem } from '../lib/api-client';

import '../features/inbox/inbox-2b.css';
import '../features/pendencies/pendencias-2b.css';

/**
 * PendenciasPage — rota `/pendencias` (Brief 2B T3 · ADR-109).
 *
 * Caixa pessoal de pendências do atendente: lista própria agrupada por prazo
 * (Vencidas · Vence hoje · Próximos 7 dias · Sem prazo · Recentemente resolvidas), com
 * resolver/adiar/retomar/criar. Fecha o loop da Coleta (a Pendency de revisão
 * gerada no finalize cai aqui).
 *
 * Decisões (spec 2026-06-06): agrupamento/contadores client-side (D2/D3); aba
 * Team é LINK p/ `/supervisor/inbox` quando há cap de supervisão (D1); sem
 * métrica pessoal fabricada (D3).
 */
export function PendenciasPage(): JSX.Element {
  return (
    <CapabilityGate
      capability="pendencies"
      fallback={
        <div className="flex flex-col h-full">
          <div className="app-pagehead">
            <div className="app-pagehead__top">
              <h1 className="app-pagehead__title">Pendências</h1>
            </div>
          </div>
          <div className="empty-state">
            <div className="empty-state__title">Sem acesso a pendências neste tenant.</div>
          </div>
        </div>
      }
    >
      <PendenciasContent />
    </CapabilityGate>
  );
}

const DUE_STATE_BY_BUCKET = {
  overdue: 'overdue',
  dueToday: 'due-soon',
  next7: 'normal',
  noDeadline: 'normal',
  resolvedRecent: 'normal',
} as const satisfies Record<string, PendencyDueState>;

/** Mensagem honesta de erro de mutation (409 conflito de versão / 422 limite / outros). */
function mutationErrorToast(err: unknown, fallback: string): void {
  const msg = err instanceof Error ? err.message : '';
  const is409 = msg.includes('409') || /conflit|version/i.test(msg);
  useToastStore.getState().push({
    variant: 'error',
    title: 'Pendência',
    description: is409
      ? 'A pendência mudou enquanto você agia — recarregando. Tente de novo.'
      : msg.length > 0
        ? msg
        : fallback,
    durationMs: 6000,
  });
}

type OpenModal = { kind: 'postpone' | 'resolve'; item: PendencyWireItem } | null;

function PendenciasContent(): JSX.Element {
  const canViewTeam = useCapability('pendency-team-read');
  const [activeTab, setActiveTab] = useState<'inbox' | 'metrics'>('inbox');
  const [filters, setFilters] = useState<PendencyFilters>(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [drawerItem, setDrawerItem] = useState<PendencyWireItem | null>(null);
  const [modal, setModal] = useState<OpenModal>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const postpone = usePostponePendency();
  const resume = useResumePendency();
  const resolve = useResolvePendency();
  const update = useUpdatePendency();

  // `status` é server-side (D2): refetch quando muda; 'all' = sem param.
  const { data, isLoading, isError, refetch } = useListMyPendencies(
    filters.status === 'all' ? {} : { status: filters.status },
  );

  // Métricas pessoais (Item 346 D3) — fetch GATEADO na aba ativa.
  const metricsQuery = useMyPendencyMetrics(activeTab === 'metrics');

  const items: PendencyWireItem[] = useMemo(() => data?.data ?? [], [data]);
  const now = Date.now();
  // Contadores/banners refletem o carregado (honesto "do carregado", D3).
  const counters = useMemo(() => deriveCounters(items, now), [items, now]);
  const maxPostponeCount = useMemo(
    () => items.filter((p) => p.postponeCount >= 3 && p.status !== 'resolved').length,
    [items],
  );

  // Filtros client-side (prioridade/categoria/prazo) + busca, depois agrupa.
  const visible = useMemo(() => {
    const filtered = applyClientFilters(
      items,
      {
        priorities: filters.priorities,
        categories: filters.categories,
        dueWindow: filters.dueWindow,
      },
      now,
    );
    const q = search.trim().toLowerCase();
    if (q === '') return filtered;
    return filtered.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q) ||
        (p.conversationId ?? '').toLowerCase().includes(q),
    );
  }, [items, filters, search, now]);

  const groups = useMemo(() => groupByDeadline(visible, now), [visible, now]);

  const okToast = (description: string): void => {
    useToastStore
      .getState()
      .push({ variant: 'success', title: 'Pendência', description, durationMs: 3500 });
  };

  const handleOpen = (item: PendencyWireItem): void => setDrawerItem(item);

  const handleAction = (kind: PendencyActionKind, item: PendencyWireItem): void => {
    switch (kind) {
      case 'postpone':
        setModal({ kind: 'postpone', item });
        break;
      case 'resolve':
        setModal({ kind: 'resolve', item });
        break;
      case 'outcome':
        setDrawerItem(item);
        break;
      case 'resume':
        resume.mutate(
          { pendencyId: item.id, expectedVersion: item.version },
          {
            onSuccess: () => okToast('Pendência retomada.'),
            onError: (err) => mutationErrorToast(err, 'Falha ao retomar.'),
          },
        );
        break;
      case 'progress':
        update.mutate(
          { pendencyId: item.id, expectedVersion: item.version, body: { status: 'in_progress' } },
          {
            onSuccess: () => okToast('Pendência marcada como em curso.'),
            onError: (err) => mutationErrorToast(err, 'Falha ao atualizar.'),
          },
        );
        break;
    }
  };

  const confirmPostpone = (p: { newDueAt: string; reason: string }): void => {
    if (modal?.kind !== 'postpone') return;
    const item = modal.item;
    postpone.mutate(
      { pendencyId: item.id, expectedVersion: item.version, body: p },
      {
        onSuccess: () => {
          setModal(null);
          okToast('Pendência adiada.');
        },
        onError: (err) => mutationErrorToast(err, 'Falha ao adiar.'),
      },
    );
  };

  const confirmResolve = (p: {
    outcome: PendencyResolutionOutcome;
    resolutionNote?: string;
  }): void => {
    if (modal?.kind !== 'resolve') return;
    const item = modal.item;
    resolve.mutate(
      { pendencyId: item.id, expectedVersion: item.version, body: p },
      {
        onSuccess: () => {
          setModal(null);
          setDrawerItem(null);
          okToast('Pendência resolvida.');
        },
        onError: (err) => mutationErrorToast(err, 'Falha ao resolver.'),
      },
    );
  };

  const subLabel =
    counters.open === 0
      ? '0 abertas'
      : `${counters.open} abertas · ${counters.overdue} vencidas · ${counters.dueToday} vence hoje`;

  const sections: { key: keyof typeof DUE_STATE_BY_BUCKET; label: string; tone?: string }[] = [
    { key: 'overdue', label: 'Vencidas', tone: 'var(--danger-fg)' },
    { key: 'dueToday', label: 'Vence hoje', tone: 'var(--warning-fg)' },
    { key: 'next7', label: 'Próximos 7 dias' },
    // N2-11: «Sem prazo» era somado em «Próximos 7 dias». Grupo próprio.
    { key: 'noDeadline', label: 'Sem prazo' },
    { key: 'resolvedRecent', label: 'Recentemente resolvidas' },
  ];

  // Filtro ativo (server-side status OU client-side OU busca): mesmo com 0
  // resultados, a rail + toolbar PRECISAM continuar visíveis para o usuário
  // limpar/alterar o filtro. O empty state "limpo" só toma a tela quando NÃO
  // há filtro algum (caixa de fato vazia).
  const hasActiveFilter =
    filters.status !== 'all' ||
    filters.priorities.length > 0 ||
    filters.categories.length > 0 ||
    filters.dueWindow !== null ||
    search.trim() !== '';
  const isEmpty = !isLoading && !isError && items.length === 0 && !hasActiveFilter;

  return (
    <div className="flex flex-col h-full">
      <div className="app-pagehead">
        <div className="app-pagehead__top">
          <h1 className="app-pagehead__title">Pendências</h1>
          <span className="app-pagehead__sub">{subLabel}</span>
          <div className="app-pagehead__actions">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setCreateOpen(true)}
            >
              <Plus size={12} /> Nova pendência
            </button>
          </div>
        </div>
      </div>

      {/* Abas: Inbox (própria) + Métricas (Item 346 D3); Time = link p/ Supervisor quando houver cap (D1). */}
      <div
        style={{
          padding: '0 var(--space-6)',
          borderBottom: '1px solid var(--border-default)',
          background: 'var(--bg-canvas)',
        }}
      >
        <div className="page-tabs">
          <button
            type="button"
            className={`page-tab${activeTab === 'inbox' ? ' page-tab--active' : ''}`}
            onClick={() => setActiveTab('inbox')}
          >
            Inbox
            <span className="page-tab__count">{counters.open}</span>
          </button>
          <button
            type="button"
            className={`page-tab${activeTab === 'metrics' ? ' page-tab--active' : ''}`}
            onClick={() => setActiveTab('metrics')}
          >
            Métricas
          </button>
          {canViewTeam && (
            <Link to="/supervisor/inbox" className="page-tab">
              Time do supervisor
            </Link>
          )}
        </div>
      </div>

      {activeTab === 'metrics' && (
        <PendencyMetricsPanel
          metrics={metricsQuery.data}
          isLoading={metricsQuery.isLoading}
          isError={metricsQuery.isError}
          onRetry={() => void metricsQuery.refetch()}
        />
      )}

      {activeTab === 'inbox' && (
        <PendencyBanners
          overdueCount={counters.overdue}
          maxPostponeCount={maxPostponeCount}
          onViewOverdue={() => setFilters((f) => ({ ...f, dueWindow: 'overdue' }))}
        />
      )}

      {activeTab === 'inbox' && (
        <>
          {/* Toolbar: busca client-side + atualizar. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-6)',
              borderBottom: '1px solid var(--border-default)',
              background: 'var(--bg-surface)',
              flexShrink: 0,
            }}
          >
            <div className="input-wrap" style={{ width: 320 }}>
              <Search size={13} aria-hidden />
              <input
                placeholder="Buscar por título, descrição, conversa…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void refetch()}>
              <RotateCw size={13} /> Atualizar
            </button>
          </div>

          {isError && (
            <div className="p-6 text-center text-text-secondary text-sm">
              Não foi possível carregar pendências.{' '}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void refetch()}
              >
                Tentar de novo
              </button>
            </div>
          )}

          {isLoading && (
            <div className="p-6 text-center text-text-secondary text-sm">Carregando…</div>
          )}

          {isEmpty && (
            <div className="empty-state">
              <div className="empty-state__glyph">
                <Coffee />
              </div>
              <div className="empty-state__title">Nenhuma pendência aberta.</div>
              <div className="empty-state__body">Bom trabalho! O dia começa limpo.</div>
            </div>
          )}

          {!isLoading && !isError && !isEmpty && (
            <div className="flex flex-1 min-h-0">
              <div className="flex-1 min-w-0 overflow-auto">
                {visible.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state__title">
                      Nenhuma pendência corresponde aos filtros.
                    </div>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => {
                        setFilters(EMPTY_FILTERS);
                        setSearch('');
                      }}
                    >
                      Limpar filtros
                    </button>
                  </div>
                ) : (
                  sections.map(({ key, label, tone }) => {
                    const bucket = groups[key];
                    if (bucket.length === 0) return null;
                    return (
                      <div key={key}>
                        <div
                          className="inbox-section"
                          style={tone !== undefined ? { color: tone } : undefined}
                        >
                          <span
                            className="inbox-section__label"
                            style={tone !== undefined ? { color: tone } : undefined}
                          >
                            {label}
                          </span>
                          <span
                            className="inbox-section__count"
                            style={tone !== undefined ? { color: tone } : undefined}
                          >
                            ({bucket.length})
                          </span>
                          <span className="inbox-section__rule" aria-hidden />
                        </div>
                        {bucket.map((item) => (
                          <PendencyCard
                            key={item.id}
                            item={item}
                            dueState={DUE_STATE_BY_BUCKET[key]}
                            onOpen={handleOpen}
                            onAction={handleAction}
                          />
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
              <PendencyFilterRail filters={filters} onChange={setFilters} />
            </div>
          )}
        </>
      )}

      {modal?.kind === 'postpone' && (
        <PostponePendencyModal
          open
          pendency={modal.item}
          submitting={postpone.isPending}
          onClose={() => setModal(null)}
          onConfirm={confirmPostpone}
        />
      )}
      {modal?.kind === 'resolve' && (
        <ResolvePendencyModal
          open
          pendency={modal.item}
          submitting={resolve.isPending}
          onClose={() => setModal(null)}
          onConfirm={confirmResolve}
        />
      )}
      {drawerItem !== null && (
        <PendencyDrawer
          pendency={drawerItem}
          onClose={() => setDrawerItem(null)}
          onAction={(kind, item) => {
            setDrawerItem(null);
            handleAction(kind, item);
          }}
        />
      )}
      <PendencyCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
